import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Check, MapPin, Plus, Star, Trash2 } from "lucide-react";

/**
 * Saved delivery addresses for a signed-in customer. Picking one fills the checkout
 * form, so a returning buyer never retypes it. The list fills itself from the
 * addresses they have ordered to before.
 */
export const SavedAddresses = ({ currentAddress, onPick, disabled = false }) => {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(true);

  const load = () => api.get("/me/addresses").then(({ data }) => setRows(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const remove = async (id, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/me/addresses/${id}`);
      setRows((p) => p.filter((r) => r.id !== id));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const saveCurrent = async () => {
    try {
      const { data } = await api.post("/me/addresses", { address: currentAddress });
      setRows((p) => [data, ...p]);
      toast.success("Address saved for next time");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const same = (a) => a.address.trim() === (currentAddress || "").trim();
  const alreadySaved = rows.some(same);

  if (rows.length === 0) {
    return currentAddress?.trim() ? (
      <button
        onClick={saveCurrent}
        disabled={disabled}
        className="text-xs text-primary hover:underline inline-flex items-center gap-1.5 mt-2 disabled:opacity-40"
        data-testid="save-address-btn"
      >
        <Plus size={12} /> Save this address for next time
      </button>
    ) : null;
  }

  return (
    <div className="rounded-xl border border-border bg-card p-4" data-testid="saved-addresses">
      <div className="flex items-center justify-between">
        <div className="overline flex items-center gap-2"><MapPin size={12} /> Your saved addresses</div>
        <button onClick={() => setOpen(!open)} className="text-xs text-muted-foreground hover:text-primary" data-testid="saved-addresses-toggle">
          {open ? "Hide" : `Show (${rows.length})`}
        </button>
      </div>

      {open && (
        <div className="grid sm:grid-cols-2 gap-3 mt-3">
          {rows.map((a) => {
            const picked = same(a);
            return (
              <button
                key={a.id}
                onClick={() => !disabled && onPick(a)}
                disabled={disabled}
                className={`text-left rounded-xl border p-3 transition-colors disabled:opacity-50 ${
                  picked ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"
                }`}
                data-testid={`saved-address-${a.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <span className="text-xs font-medium inline-flex items-center gap-1.5">
                    {a.is_default && <Star size={10} className="text-secondary" fill="currentColor" />}
                    {a.label || a.name || "Delivery address"}
                  </span>
                  <span className="flex items-center gap-2 shrink-0">
                    {picked && <Check size={13} className="text-primary" />}
                    <Trash2
                      size={12}
                      className="text-muted-foreground hover:text-destructive"
                      onClick={(e) => remove(a.id, e)}
                      data-testid={`saved-address-del-${a.id}`}
                    />
                  </span>
                </div>
                <p className="text-[11px] text-muted-foreground mt-1.5 line-clamp-3">{a.address}</p>
                {a.phone ? <p className="text-[11px] text-muted-foreground mt-1">{a.phone}</p> : null}
              </button>
            );
          })}
        </div>
      )}

      {open && !alreadySaved && currentAddress?.trim() && (
        <button
          onClick={saveCurrent}
          disabled={disabled}
          className="text-xs text-primary hover:underline inline-flex items-center gap-1.5 mt-3 disabled:opacity-40"
          data-testid="save-address-btn"
        >
          <Plus size={12} /> Save the address above for next time
        </button>
      )}
    </div>
  );
};

export default SavedAddresses;
